package puzzlekey

import kotlin.math.abs
import kotlin.math.sqrt

/** How many sign projections a Scan yields; the mask picks the stable ones, which are the Puzzle bits (ADR-0009 §1). */
const val PROJECTIONS = 512

/**
 * The projection seed `seal` enrolls with and records in `sealed.json`: the one ADR-0011's gates and
 * measurements used. Another seed means new projections, so a new mask that must pass the gates again.
 */
const val PROJECTION_SEED = 1

/** Error tolerance defaults (ADR-0009 §4): up to one flipped bit, five candidates per frame. */
const val DEFAULT_MAX_ERRORS = 1
const val DEFAULT_CANDIDATES_PER_FRAME = 5

/**
 * PBKDF2-SHA256 iterations on the Puzzle route (ADR-0012): ≈ 170 ms per candidate on the iPhone 12 mini,
 * so [DEFAULT_CANDIDATES_PER_FRAME] fit in under a second per frame. `seal` records it in `sealed.json`;
 * changing it means a re-seal.
 */
const val PBKDF2_ITERATIONS = 1_000_000

/**
 * Turns an embedding into [count] projection values (ADR-0009 §1): L2-normalise, subtract the public
 * [centre], project onto random directions drawn from [seed]. A value's sign is that bit; its
 * magnitude is the bit's confidence.
 *
 * The directions are Irwin–Hall normals (sum of 12 uniforms − 6) from xoshiro128**, so they come out
 * bit-for-bit the same on the JVM and in JS: only 32-bit integer steps and exact sums of doubles.
 */
class Projector(private val centre: FloatArray, seed: Int, val count: Int = PROJECTIONS) {
    private val dim = centre.size
    private val directions = DoubleArray(count * dim).also { w ->
        val random = Xoshiro128(seed)
        for (i in w.indices) {
            var sum = 0.0
            repeat(12) { sum += random.nextUnit() }
            w[i] = sum - 6
        }
    }

    /** Projection [index]'s direction, one weight per embedding dimension. Public helper data, like the seed. */
    fun weights(index: Int): DoubleArray = directions.copyOfRange(index * dim, (index + 1) * dim)

    fun project(embedding: FloatArray): DoubleArray {
        require(embedding.size == dim) { "expected a $dim-float embedding, got ${embedding.size}" }
        var norm = 0.0
        for (v in embedding) norm += v.toDouble() * v
        norm = sqrt(norm)
        val e = DoubleArray(dim) { embedding[it] / norm - centre[it] }
        return DoubleArray(count) { b ->
            var sum = 0.0
            val offset = b * dim
            for (i in 0 until dim) sum += e[i] * directions[offset + i]
            sum
        }
    }
}

/**
 * What Enrollment learns (ADR-0009 §2). [mask] (ascending bit indices) is public helper data;
 * [reference] (those bits' values, in mask order) *is* the Puzzle bits and never leaves `seal`.
 */
class Enrollment(val mask: IntArray, val reference: BooleanArray)

/** Enrollment from the projections of many Scans: the mask is every bit on which all of them agree (τ = 1). */
fun enroll(projections: List<DoubleArray>): Enrollment {
    require(projections.isNotEmpty()) { "Enrollment needs at least one frame" }
    val first = projections.first()
    val mask = first.indices.filter { b -> projections.all { (it[b] > 0) == (first[b] > 0) } }.toIntArray()
    return Enrollment(mask, BooleanArray(mask.size) { first[mask[it]] > 0 })
}

/**
 * The Puzzle-bit guesses for one Scan (ADR-0009 §4), in the order the site tries them: the scanned
 * masked bits first, then flips of up to [maxErrors] of the least-confident masked bits, cheapest total
 * confidence first, [candidatesPerFrame] guesses at most.
 *
 * A flip set that includes the r-th least-confident bit costs at least as much as the r cheaper single
 * flips and the unflipped scan, so only the [candidatesPerFrame] least-confident bits can take part.
 * The search enumerates their subsets, which stays small for the tolerances ADR-0009 has in mind.
 */
fun candidates(projection: DoubleArray, mask: IntArray, maxErrors: Int, candidatesPerFrame: Int): List<BooleanArray> {
    require(maxErrors >= 0 && candidatesPerFrame >= 1) { "maxErrors $maxErrors, candidatesPerFrame $candidatesPerFrame" }
    val scanned = BooleanArray(mask.size) { projection[mask[it]] > 0 }
    val confidence = DoubleArray(mask.size) { abs(projection[mask[it]]) }
    val leastConfident = mask.indices.sortedWith(compareBy({ confidence[it] }, { it })).take(candidatesPerFrame)

    val flipSets = mutableListOf<List<Int>>()
    fun extend(set: List<Int>, from: Int) {
        flipSets += set
        if (set.size < maxErrors) for (k in from until leastConfident.size) extend(set + leastConfident[k], k + 1)
    }
    extend(emptyList(), 0)

    return flipSets
        .sortedWith(compareBy({ set -> set.sumOf { confidence[it] } }, { it.size }))
        .take(candidatesPerFrame)
        .map { set -> scanned.copyOf().also { bits -> for (p in set) bits[p] = !bits[p] } }
}

/** Puzzle bits as key material: eight bits a byte, most significant first, the last byte padded with zeros. */
fun packBits(bits: BooleanArray): ByteArray {
    val out = ByteArray((bits.size + 7) / 8)
    for (i in bits.indices) if (bits[i]) out[i / 8] = (out[i / 8].toInt() or (0x80 ushr (i % 8))).toByte()
    return out
}

/** xoshiro128** (Blackman & Vigna), seeded through SplitMix32. Int arithmetic only, so JS and the JVM agree. */
private class Xoshiro128(seed: Int) {
    private var s0: Int
    private var s1: Int
    private var s2: Int
    private var s3: Int

    init {
        var x = seed
        fun splitMix(): Int {
            x += -0x61c88647 // 0x9e3779b9
            var z = x
            z = (z xor (z ushr 16)) * -0x7a143595 // 0x85ebca6b
            z = (z xor (z ushr 13)) * -0x3d4d51cb // 0xc2b2ae35
            return z xor (z ushr 16)
        }
        s0 = splitMix(); s1 = splitMix(); s2 = splitMix(); s3 = splitMix()
    }

    fun nextInt(): Int {
        val result = (s1 * 5).rotateLeft(7) * 9
        val t = s1 shl 9
        s2 = s2 xor s0
        s3 = s3 xor s1
        s1 = s1 xor s2
        s0 = s0 xor s3
        s2 = s2 xor t
        s3 = s3.rotateLeft(11)
        return result
    }

    /** Uniform in [0, 1), from the top 31 bits. */
    fun nextUnit(): Double = (nextInt() ushr 1) / 2147483648.0
}
